import React, { useMemo, useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { StatusBadge } from '../../components/StatusBadge';
import { getShiftModuleCapabilities } from '../../config/shiftCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import { deactivateShift, getShiftDetails } from '../../services/shiftService';
import type { ShiftDetails } from '../../types/shift';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { formatMinutes, formatPermissionTime } from '../../utils/dateTime';
import { shiftWorkPolicyErrorMessage } from '../../utils/shiftWorkPolicyErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'ShiftDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

function minutesLabel(value: number | null | undefined): string | null {
  if (value == null) return null;
  const formatted = formatMinutes(value);
  return formatted === 'Not available' ? null : formatted;
}

export function ShiftDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getShiftModuleCapabilities(auth.permissions);
  const { shiftId } = route.params;
  const [item, setItem] = useState<ShiftDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const data = await getShiftDetails(shiftId);
        setItem(data);
        setError(null);
      } catch (caught) {
        setItem(null);
        setError(
          shiftWorkPolicyErrorMessage(
            caught,
            'The requested shift could not be loaded.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [shiftId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.SHIFT_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Shift"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading shift" />
        </AppScreen>
      ) : error && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Shift"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : item ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title={item.shiftName}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <StatusBadge label={item.statusLabel} />
          <AppCard style={styles.card}>
            {capabilities.canUpdateShift &&
            isActiveConfigurationRecord(item.isActive) ? (
              <ConfigurationDeleteButton
                entityLabel="shift"
                loading={softDelete.busy}
                disabled={softDelete.busy}
                onPress={() =>
                  softDelete.confirmAndDelete({
                    entityLabel: 'shift',
                    recordName: item.shiftName,
                    deleteRecord: async () => {
                      await deactivateShift(item);
                    },
                    onSuccess: () => navigation.goBack(),
                    errorMessage: caught =>
                      shiftWorkPolicyErrorMessage(
                        caught,
                        'Unable to delete shift.',
                      ),
                  })
                }
              />
            ) : null}
            <Field palette={palette} label="Shift code" value={item.shiftCode} />
            <Field palette={palette} label="Shift name" value={item.shiftName} />
            <Field palette={palette} label="Description" value={item.description} />
            <Field palette={palette} label="Location" value={item.locationName} />
            <Field palette={palette} label="Start time" value={formatPermissionTime(item.shiftStartTime)} />
            <Field palette={palette} label="End time" value={formatPermissionTime(item.shiftEndTime)} />
            <Field
              palette={palette}
              label="Crosses midnight"
              value={item.crossesMidnight ? 'Yes' : 'No'}
            />
            <Field
              palette={palette}
              label="Expected work duration"
              value={minutesLabel(item.expectedWorkMinutes)}
            />
            <Field
              palette={palette}
              label="Lunch break duration"
              value={minutesLabel(item.lunchBreakMinutes)}
            />
            <Field
              palette={palette}
              label="Spread duration"
              value={minutesLabel(item.spreadMinutes)}
            />
            <Field
              palette={palette}
              label="Minimum half day"
              value={minutesLabel(item.minimumHalfDayMinutes)}
            />
            <Field
              palette={palette}
              label="Late grace (in)"
              value={minutesLabel(item.lateGraceMinutes)}
            />
            <Field
              palette={palette}
              label="Early exit grace (out)"
              value={minutesLabel(item.earlyExitGraceMinutes)}
            />
            <Field
              palette={palette}
              label="Check-in open"
              value={minutesLabel(item.checkInOpenMinutes)}
            />
            <Field
              palette={palette}
              label="Check-in close"
              value={minutesLabel(item.checkInCloseMinutes)}
            />
            <Field
              palette={palette}
              label="Check-out open"
              value={minutesLabel(item.checkOutOpenMinutes)}
            />
            <Field
              palette={palette}
              label="Check-out close"
              value={minutesLabel(item.checkOutCloseMinutes)}
            />
            <Field
              palette={palette}
              label="Auto checkout"
              value={item.autoCheckoutEnabled ? 'Yes' : 'No'}
            />
            <Field
              palette={palette}
              label="Auto checkout after"
              value={minutesLabel(item.autoCheckoutAfterMinutes)}
            />
            <Field
              palette={palette}
              label="Flexible shift"
              value={item.isFlexibleShift ? 'Yes' : 'No'}
            />
            <Field palette={palette} label="Default shift" value={item.isDefault ? 'Yes' : 'No'} />
            <Field palette={palette} label="Status" value={item.statusLabel} />
          </AppCard>
          <View style={styles.actions}>
            {capabilities.canUpdateShift ? (
              <AppButton
                title="Edit"
                onPress={() =>
                  navigation.navigate(routes.shiftEdit, { shiftId })
                }
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.md, marginTop: spacing.md },
  field: { gap: spacing.xs },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
});
